import {
  HeaderSection,
  SingleRow,
  SingleRowWithCopy,
} from '@/features/ap_payees/components/PayeeDetailDrawer/components/Common';
import { WebReceivingMethodData } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import classnames from 'classnames/bind';
import { FC, useMemo } from 'react';
import parentStyles from '../../../../styles.module.scss';
import localStyles from './styles.module.scss';

const cx = classnames.bind({ ...parentStyles, ...localStyles });

type Props = {
  data?: WebReceivingMethodData;
};
export const WebReceivingMethod: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { is_payee_active, supplierUploadSetting = {} } = data || {};
  const {
    is_accepted_invitation,
    is_published_invitation_url,
    is_expired_invitation_url,
    tenant_display_name,
    expiration_datetime,
    invitation_url,
  } = supplierUploadSetting || {};

  const isRegistered = !!is_accepted_invitation;

  const isExpired = !!is_expired_invitation_url;
  const isPublished = !!is_published_invitation_url;

  const renderTenantDisplayNameWithWarningMessage = useMemo(
    () => (
      <div>
        <div className={cx('tenantDisplayName')}>{tenant_display_name}</div>
        <div className={cx('warningMessage')}>
          {t(
            'ap_payee_detail_drawer_web_receive_register_office_name_is_not_active'
          )}
        </div>
      </div>
    ),
    [t, tenant_display_name]
  );

  const rowRenderData = useMemo(() => {
    const rowData = [];
    if (!isRegistered) {
      if (!isPublished) {
        rowData.push({
          label: t('ap_payee_detail_drawer_web_receive_account_url'),
          value: t('ap_payee_detail_drawer_web_receive_account_url_info'),
          ComponentRender: SingleRow,
          contentClassName: cx('secondaryText'),
        });
      } else {
        if (!isExpired) {
          rowData.push({
            label: t('ap_payee_detail_drawer_web_receive_account_url'),
            value: invitation_url,
            ComponentRender: SingleRowWithCopy,
          });
          rowData.push({
            label: t(
              'ap_payee_detail_drawer_web_receive_account_url_expiration_date'
            ),
            value: expiration_datetime,
            ComponentRender: SingleRow,
          });
        } else {
          rowData.push({
            label: t('ap_payee_detail_drawer_web_receive_account_url'),
            value: t('ap_payee_detail_drawer_web_receive_account_url_expired'),
            ComponentRender: SingleRow,
            contentClassName: cx('secondaryText'),
          });
        }
      }
      rowData.push({
        label: t('ap_payee_detail_drawer_web_receive_invitation_status'),
        ComponentRender: SingleRow,
        value: t(
          'ap_payee_detail_drawer_web_receive_invitation_status_not_registered'
        ),
      });
    }
    if (isRegistered) {
      rowData.push({
        label: t('ap_payee_detail_drawer_web_receive_invitation_status'),
        value: t(
          'ap_payee_detail_drawer_web_receive_invitation_status_registered'
        ),
        ComponentRender: SingleRow,
      });
      if (is_payee_active) {
        rowData.push({
          label: t('ap_payee_detail_drawer_web_receive_register_office_name'),
          value: tenant_display_name,
          ComponentRender: SingleRow,
        });
      }
      if (!is_payee_active) {
        rowData.push({
          label: t('ap_payee_detail_drawer_web_receive_register_office_name'),
          renderContent: renderTenantDisplayNameWithWarningMessage,
          ComponentRender: SingleRow,
        });
      }
    }
    return rowData;
  }, [
    renderTenantDisplayNameWithWarningMessage,
    is_payee_active,
    tenant_display_name,
    invitation_url,
    expiration_datetime,
    t,
    isRegistered,
    isPublished,
    isExpired,
  ]);

  return (
    <div className={cx('subSectionContainer')}>
      <HeaderSection
        displayName={t('ap_payee_detail_drawer_web_receiving_method')}
        isSubHeader
      />
      <div className={cx('bodySectionContainer')}>
        {rowRenderData.map((item, index) => (
          <item.ComponentRender
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value ?? ''}
            renderContent={item.renderContent}
            contentClassName={item?.contentClassName}
          />
        ))}
      </div>
    </div>
  );
};

WebReceivingMethod.displayName = 'WebReceivingMethod';
